import React, { useEffect, useState } from "react";
import { Shield, BarChart3, Target, Cookie, X, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

type ConsentType = "necessary" | "analytics" | "marketing";

const CookiesBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const { t } = useTranslation(); // Assuming you have a translation function
  // Check localStorage on component mount
  useEffect(() => {
    const storedConsent = localStorage.getItem("cookiesAccepted");
    if (storedConsent) {
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleSwitchChange = (type: ConsentType) => {
    if (type === "necessary") return; // Can't change necessary cookies

    setConsent((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setShowBanner(false);
    }, 300);
  };

  const handleAcceptAll = () => {
    setConsent({
      necessary: true,
      analytics: true,
      marketing: true,
    });
    localStorage.setItem(
      "cookiesAccepted",
      JSON.stringify({
        necessary: true,
        analytics: true,
        marketing: true,
        timestamp: Date.now(),
      })
    );
    handleClose();
  };

  const handleRejectAll = () => {
    setConsent({
      necessary: true,
      analytics: false,
      marketing: false,
    });
    localStorage.setItem(
      "cookiesAccepted",
      JSON.stringify({
        necessary: true,
        analytics: false,
        marketing: false,
        timestamp: Date.now(),
      })
    );
    handleClose();
  };

  const handleAcceptSelection = () => {
    localStorage.setItem(
      "cookiesAccepted",
      JSON.stringify({
        ...consent,
        timestamp: Date.now(),
      })
    );
    handleClose();
  };

  const cookieTypes = [
    {
      key: "necessary" as ConsentType,
      title: t("cookiesBanner.cookieTypes.necessary.title"),
      description: t("cookiesBanner.cookieTypes.necessary.description"),
      icon: Shield,
      color: "from-emerald-500 to-teal-600",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
      required: true,
    },
    {
      key: "analytics" as ConsentType,
      title: t("cookiesBanner.cookieTypes.analytics.title"),
      description: t("cookiesBanner.cookieTypes.analytics.description"),
      icon: BarChart3,
      color: "from-blue-500 to-indigo-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      required: false,
    },
    {
      key: "marketing" as ConsentType,
      title: t("cookiesBanner.cookieTypes.marketing.title"),
      description: t("cookiesBanner.cookieTypes.marketing.description"),
      icon: Target,
      color: "from-purple-500 to-pink-600",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
      required: false,
    },
  ];

  if (!showBanner) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm   flex items-end sm:items-center justify-center z-[9999] transition-all duration-300 ${isAnimating ? "opacity-0" : "opacity-100"
        }`}
      onClick={(e) =>
        e.target === e.currentTarget && !showDetails && handleClose()
      }
    >
      <div
        className={`bg-white/95 backdrop-blur-xl rounded-t-3xl sm:rounded-3xl shadow-2xl border absolute bottom-10 border-white/20 w-full sm:w-[90%] max-w-4xl mx-4 transform transition-all duration-500 ease-out ${isAnimating
          ? "translate-y-full sm:translate-y-8 opacity-0 scale-95"
          : "translate-y-0 opacity-100 scale-100"
          } ${showDetails ? "sm:max-h-[90vh]" : ""}`}
      >
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Cookie className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t("cookiesBanner.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("cookiesBanner.subtitle")}
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 leading-relaxed mb-6">
            {t("cookiesBanner.description")}
          </p>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {t("cookiesBanner.buttons.acceptAll")}
            </button>
            <button
              onClick={handleRejectAll}
              className="flex-1 sm:flex-none border-2 border-gray-200 hover:border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:bg-gray-50"
            >
              {t("cookiesBanner.buttons.rejectAll")}
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 border-2 border-blue-200 hover:border-blue-300 text-blue-700 px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:bg-blue-50"
            >
              <Settings className="w-4 h-4" />
              {t("cookiesBanner.buttons.customize")}
            </button>
          </div>

          {/* Detailed Settings */}
          <div
            className={`transition-all duration-500 ease-out overflow-hidden ${showDetails ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
              }`}
          >
            <div className="space-y-4 pt-4 border-t border-gray-100">
              {cookieTypes.map((cookie) => {
                const IconComponent = cookie.icon;
                const isEnabled = consent[cookie.key];

                return (
                  <div
                    key={cookie.key}
                    className={`${cookie.bgColor} rounded-2xl p-4 transition-all duration-200 hover:shadow-md`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 bg-gradient-to-br ${cookie.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}
                      >
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3
                            className={`font-bold text-lg ${cookie.textColor}`}
                          >
                            {cookie.title}
                            {cookie.required && (
                              <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                                Required
                              </span>
                            )}
                          </h3>

                          <button
                            onClick={() => handleSwitchChange(cookie.key)}
                            disabled={cookie.required}
                            className={`relative w-12 h-7 rounded-full transition-all duration-200 ${isEnabled
                              ? "bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg"
                              : "bg-gray-300"
                              } ${cookie.required
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer hover:scale-105"
                              }`}
                          >
                            <div
                              className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${isEnabled ? "left-6" : "left-1"
                                }`}
                            />
                          </button>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {cookie.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 px-1 ">
                <button
                  onClick={handleAcceptSelection}
                  className="w-full bg-gradient-to-r mb-4 from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 rounded-xl font-semibold transition-all duration-200 transform shadow-lg hover:shadow-xl"
                >
                  {t("cookiesBanner.buttons.savePreferences")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-gray-500">
            {t("cookiesBanner.footer.text")}{" "}
            <a
              href="/legal#privacy"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              {t("cookiesBanner.footer.privacyPolicy")}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiesBanner;
